/**
 * Reading a form — and the page around it — in one language.
 *
 * THE INSIGHT THIS FILE EXISTS FOR. A Spanish-speaking field hand touches exactly one screen: the
 * unauthenticated QR inspection page. And what they actually read there is barely the app chrome — it
 * is the FORM CONTENT. "Fluid leaks on the ground — oil, fuel, coolant, and hydraulic fluid", and
 * thirty-five more like it. Every one of those strings is TENANT-OWNED DATA in a JSONB column, not
 * code, so translating the application perfectly would still leave an operator reading English safety
 * questions. That is why the form resolvers below come first and the chrome dictionary comes second.
 *
 * ONE RULE: **a missing translation shows the base language.** Never a blank, never a key. A tenant
 * will always translate most of a sheet and leave the rest, and the rest is never the part you would
 * have chosen. A blank line on a safety checklist reads as "there is no question here", which is the
 * one thing it must never say — so every lookup uses `||` and treats present-but-empty exactly like
 * absent.
 *
 * The mirror of this file is src/modules/forms/backend/app/services/i18n.py, which is what renders the
 * archived PDF. If one grows a rule the other does not have, the page and the filed record start
 * disagreeing about what a safety document says.
 *
 * ── WHAT MOVED, AND WHAT DID NOT ─────────────────────────────────────────────────────────────────
 * This file was two things bolted together and only one of them was ever forms-specific. The LANGUAGE
 * PRIMITIVES — the base language, the name table, the non-blank rule, the string-by-string chrome
 * getter, the remembered choice and the precedence chain — were app-wide already; only the path said
 * otherwise. They now live in src/frontend/src/shared/i18n/ and are RE-EXPORTED here, so nothing that
 * imports from this module changed one character and the QR page's tests still pin the same
 * behaviour. What stayed is what actually reads a tenant's JSONB document: `nodeId`, `hydrate`,
 * `bundleLanguages` and the `t*` resolvers.
 *
 * The re-exports are not a compatibility shim to be cleaned up later — they are the point. One
 * implementation of "which language, and what if it is missing" is the whole reason the QR page and
 * the application cannot drift apart.
 */
export {
  BASE_LANGUAGE, LANGUAGE_NAMES, languageName, matchLanguage, primary,
} from '../../shared/i18n/languages'

// `<html lang>`. A sheet decides its own language from its own offered set, so the page that renders
// one says so and the application chain yields while it is on screen — see
// shared/i18n/documentLanguage.js. Re-exported here for the same reason everything else above is: this
// page's import block stays inside its own module, and there is one implementation of the rule.
export {
  claimDocumentLanguage, releaseDocumentLanguage,
} from '../../shared/i18n/documentLanguage'

import {
  BASE_LANGUAGE, PUBLIC_LANGUAGE_STORAGE_KEY, resolveLanguage,
} from '../../shared/i18n/languages'

// ── reading the form's own strings ────────────────────────────────────────────────────────────────

/** A usable translation is a non-blank string; anything else means "not translated". */
function clean(v) {
  return typeof v === 'string' && v.trim() ? v : null
}

/**
 * One node's translation map for `lang`, or {}.
 *
 * `node.i18n` is attached by `hydrate` below, onto the objects this page RENDERS. It is not in the
 * stored schema and must never be written back into one: a document carrying it cannot be parsed by an
 * image that predates it, and on this page a parse failure is 404 "That sticker isn't valid" for every
 * sticker in the tenant. The strings arrive in a sibling payload and are merged onto render objects.
 */
function entry(node, lang) {
  if (!node || !lang) return {}
  const table = node.i18n
  if (!table || typeof table !== 'object') return {}
  const e = table[lang]
  return e && typeof e === 'object' ? e : {}
}

/**
 * Whether a translation still belongs to the string it was made from.
 *
 * THE FAILURE A MISSING TRANSLATION DOES NOT HAVE: a wrong one. A translation is bound to a KEY — a row
 * key, an option value, a column key — and a key does not move when its label is rewritten. That
 * binding is what keeps a Spanish submission byte-identical to an English one, and it is also what
 * leaves the old Spanish attached to the new English when a tenant repurposes a copied starter's row.
 * Both starters say "copy and adapt" and `duplicate` copies schema_json verbatim, so that edit is the
 * expected path.
 *
 * A missing translation degrades visibly to English. A stale one asks the operator standing at the
 * machine a DIFFERENT QUESTION from the one the office reads back off the same row key, on a sheet
 * where `defect` takes the machine out of service — and nothing on the page says the two disagree.
 *
 * Recording NOTHING means "unknown", and unknown is trusted: every translation written before `_src`
 * existed records nothing, and treating unknown as stale would un-translate every sheet in the field.
 */
function fresh(recorded, base) {
  if (typeof recorded !== 'string') return true
  return recorded.trim() === String(base || '').trim()
}

/** What this node's `role` was translated from, or undefined when it was not recorded. */
function source(node, lang, role) {
  const src = entry(node, lang)._src
  return src && typeof src === 'object' ? src[role] : undefined
}

/** A translated string for a plain slot, dropped when its recorded source no longer matches `base`. */
function slot(node, lang, role, base) {
  const v = clean(entry(node, lang)[role])
  return v && fresh(source(node, lang, role), base) ? v : null
}

function mapped(node, lang, role, key, base) {
  const sub = entry(node, lang)[role]
  const v = sub && typeof sub === 'object' ? clean(sub[key]) : null
  if (!v) return null
  const src = source(node, lang, role)
  const recorded = src && typeof src === 'object' ? src[key] : undefined
  return fresh(recorded && typeof recorded === 'object' ? recorded.label : recorded, base) ? v : null
}

/**
 * Every language a version offers, base first.
 *
 * Derived from what actually EXISTS rather than from a declaration. The old shape stored a `languages`
 * list inside the schema and this function had to defend against it going stale — a builder whose
 * serializer forgot it made a tenant's Spanish unreachable while it still sat in the elements. "The
 * base language, plus the languages that have a row" cannot go stale: adding a language IS adding the
 * row, and removing it IS deleting the row.
 */
export function bundleLanguages(translations, base) {
  const b = base || BASE_LANGUAGE
  const found = Object.keys(translations || {}).filter((t) => typeof t === 'string' && t && t !== b)
  return [b, ...found.sort()]
}

/**
 * The node id a translation is filed under — `field:<key>` for anything carrying an answer,
 * `element:<id>` for a design element, `section:<key>` for a v1 section.
 *
 * Mirrors services/i18n.py `node_id`. Keying off the FIELD KEY (and, inside a node, off row key,
 * option VALUE and column key) is what makes a sheet filled in Spanish store byte-identical answers to
 * the same sheet filled in English — a translation can never move a key or a value.
 */
export function nodeId(node) {
  if (node?.kind !== undefined) return node.kind === 'field' ? `field:${node.key}` : `element:${node.id}`
  if (node?.type !== undefined) return `field:${node.key}`
  return `section:${node?.key}`
}

/**
 * Attach one node's strings, from the version's translation bundle, onto a RENDER object.
 *
 * The merge happens here — on objects that exist only to be drawn — and never into the fetched schema.
 * That dict is the stored document; putting the strings back into it is exactly the coupling this whole
 * arrangement removes. `pick` remaps the slots for the one node whose roles differ from what the
 * renderer draws (a v1 section heading is a `title`, drawn as a `text`).
 */
export function hydrate(node, translations, id = null, pick = null) {
  const i18n = {}
  Object.entries(translations || {}).forEach(([lang, strings]) => {
    const e = strings?.nodes?.[id || nodeId(node)]
    if (!e) return
    const got = pick ? pick(e) : e
    if (got) i18n[lang] = got
  })
  return { ...node, i18n }
}

/**
 * A repeating table COLUMN's own strings, in the shape an element's entry has, or null.
 *
 * A column has no node id: its strings sit nested inside the parent element's entry under
 * `fields[<column key>]`. Mirrors the backend's `_column_bundle` AND its shorthand, where a plain
 * string means "just the heading" and the source for it is recorded one level up on the parent —
 * which is the form every bundle written before nested columns existed is in.
 */
function columnStrings(strings, parentId, colKey) {
  const e = strings?.nodes?.[parentId]
  const fields = e && typeof e === 'object' ? e.fields : null
  const got = fields && typeof fields === 'object' ? fields[colKey] : null
  if (typeof got === 'string') {
    const psrc = e && typeof e._src === 'object' ? e._src.fields : null
    const rec = psrc && typeof psrc === 'object' ? psrc[colKey] : undefined
    const label = rec && typeof rec === 'object' ? rec.label : rec
    return typeof label === 'string' ? { label: got, _src: { label } } : { label: got }
  }
  return got && typeof got === 'object' ? got : null
}

/**
 * The SAME document, with every human-readable string resolved into `lang` — for RENDERING only.
 *
 * WHY THIS EXISTS AND WHAT IT IS NOT. The QR sticker page reads a tenant's questions in Spanish and
 * the archived PDF prints them in Spanish, and the logged-in fill screen — the one a FOREMAN uses —
 * showed the same document in English, from the same translation rows. That is the strategic point of
 * the whole feature missed at the last step: a form's questions are tenant data in a JSONB column, so
 * translating the application perfectly still leaves the operator reading English safety questions.
 *
 * It is a RENDER COPY. Nothing here may be saved: the stored document is the record, the answers are
 * keyed off `key` / row key / option VALUE, and NONE of those is touched — only the words beside
 * them. So a sheet filled in Spanish stores exactly what the same sheet filled in English stores, the
 * rules engine and the calculated fields still see the values they were written against, and a screen
 * that hands this copy to a validator is validating the same document.
 *
 * It resolves through `tLabel`/`tHelp`/`tText`/`tRow`/`tOptions` rather than reading the bundle
 * itself, so it inherits the two rules those already enforce and cannot grow a third: a missing
 * translation shows the base language, and a STALE one — written against wording that has since been
 * edited — is dropped rather than shown.
 *
 * Returns the ORIGINAL object when nothing changed, so a caller with no translations pays nothing and
 * memoised consumers below it do not re-render.
 */
export function localizeSchema(schema, translations, lang) {
  if (!schema || !lang || !translations) return schema
  const strings = translations[lang] || translations[String(lang).split('-')[0]]
  if (!strings || typeof strings !== 'object') return schema

  let changed = false
  const swap = (was, now) => {
    if (typeof now === 'string' && now !== was) { changed = true; return now }
    return was
  }

  const localizeOptions = (node, h) => {
    const opts = (node?.validation || {}).options
    if (!Array.isArray(opts) || !opts.length) return node.validation
    const resolved = tOptions(h, lang)
    const next = opts.map((o, i) => {
      const label = resolved[i]?.label
      if (o && typeof o === 'object') {
        const got = swap(o.label ?? String(o.value), label)
        return got === o.label ? o : { ...o, label: got }
      }
      // A bare-string option IS its own value. Widening it to {value,label} here would change what
      // the renderer sends; it is left alone, which is the same answer the untranslated page gives.
      return o
    })
    return next.some((o, i) => o !== opts[i]) ? { ...node.validation, options: next } : node.validation
  }

  const localizeColumns = (node, id, key) => {
    const cols = node[key]
    if (!Array.isArray(cols) || !cols.length) return cols
    const next = cols.map((col) => {
      if (!col || typeof col !== 'object' || !col.key) return col
      const h = { ...col, i18n: { [lang]: columnStrings(strings, id, col.key) || {} } }
      const label = swap(col.label, tLabel(h, lang))
      const validation = localizeOptions(col, h)
      return label === col.label && validation === col.validation ? col : { ...col, label, validation }
    })
    return next.some((c, i) => c !== cols[i]) ? next : cols
  }

  const localizeNode = (node) => {
    if (!node || typeof node !== 'object') return node
    const id = nodeId(node)
    const h = hydrate(node, translations, id)

    if (node.kind === 'text') {
      const text = swap(node.text, tText(h, lang))
      return text === node.text ? node : { ...node, text }
    }

    // A v1 SECTION carries neither `kind` nor `type`, and its `fields` are the section's QUESTIONS,
    // not a repeating table's columns. Reading them as columns would rewrite a whole section into one
    // widget's headings — so the distinction is made once, here.
    const isSection = node.kind === undefined && node.type === undefined

    const out = { ...node }
    if (typeof node.title === 'string') out.title = swap(node.title, slot(h, lang, 'title', node.title) || node.title)
    if (typeof node.label === 'string') out.label = swap(node.label, tLabel(h, lang))
    if (typeof node.help === 'string') out.help = swap(node.help, tHelp(h, lang))

    if (Array.isArray(node.rows) && node.rows.length) {
      const rows = node.rows.map((r) => {
        if (!r || typeof r !== 'object') return r
        const label = swap(r.label, tRow(h, r, lang))
        return label === r.label ? r : { ...r, label }
      })
      if (rows.some((r, i) => r !== node.rows[i])) out.rows = rows
    }

    if (!isSection) {
      const validation = localizeOptions(node, h)
      if (validation !== node.validation) out.validation = validation
      if (Array.isArray(node.columns)) out.columns = localizeColumns(node, id, 'columns')
      if (Array.isArray(node.fields)) out.fields = localizeColumns(node, id, 'fields')
    }
    return out
  }

  let next = schema
  if (Array.isArray(schema.elements)) {
    const elements = schema.elements.map(localizeNode)
    if (elements.some((el, i) => el !== schema.elements[i])) next = { ...schema, elements }
  } else if (Array.isArray(schema.sections)) {
    const sections = schema.sections.map((section) => {
      const s = localizeNode(section)
      const fields = Array.isArray(section?.fields) ? section.fields.map(localizeNode) : section?.fields
      const moved = Array.isArray(fields) && fields.some((f, i) => f !== section.fields[i])
      if (s === section && !moved) return section
      return moved ? { ...s, fields } : s
    })
    if (sections.some((s, i) => s !== schema.sections[i])) next = { ...schema, sections }
  }
  return changed ? next : schema
}

/** The sheet's own title/description live on the template ROW, so their strings sit beside the nodes
 *  rather than under one. This wraps them in the shape the resolvers read. */
export function documentNode(translations) {
  const i18n = {}
  Object.entries(translations || {}).forEach(([lang, strings]) => {
    if (strings?.document) i18n[lang] = strings.document
  })
  return { i18n }
}

/** A field's heading — also a checklist grid's heading. Falls back past the label to the key, which is
 *  what the untranslated renderer already did, so no path can produce an empty heading. */
export function tLabel(node, lang) {
  return slot(node, lang, 'label', node?.label) || node?.label || node?.key || ''
}

export function tHelp(node, lang) {
  return slot(node, lang, 'help', node?.help) || node?.help || ''
}

/** A `kind: "text"` design element: the section bands, the instructions, the out-of-service warning,
 *  and the attestation the operator signs. */
export function tText(node, lang) {
  return slot(node, lang, 'text', node?.text) || node?.text || ''
}

/** One checklist item, keyed by the ROW KEY — the durable identity of that question and of its stored
 *  answer, so a translation cannot drift onto a different row. */
export function tRow(field, row, lang) {
  return mapped(field, lang, 'rows', row?.key, row?.label) || row?.label || row?.key || ''
}

/**
 * The text on one choice, keyed by the option VALUE.
 *
 * Never by position and never by the English label. This is the hinge of the whole design: the button
 * reads "Falla" and its value is still `defect`, so the validator, the rules engine and the stored
 * record cannot tell which language the sheet was filled in.
 */
export function tOption(field, value, lang) {
  const key = String(value)
  let base = null
  for (const o of ((field?.validation || {}).options || [])) {
    if (o && typeof o === 'object') {
      if (String(o.value) === key) { base = o.label || key; break }
    } else if (String(o) === key) {
      // A bare-string option is its own label — there is no `label` slot to hang a translation on, so
      // the `options` map is the only way such an option is ever translatable at all.
      base = key
      break
    }
  }
  return mapped(field, lang, 'options', key, base) || base || key
}

/** Every choice as {value, label} in `lang`, in schema order — the shape a renderer wants. */
export function tOptions(field, lang) {
  const raw = (field?.validation || {}).options || []
  return raw.map((o) => {
    const value = o && typeof o === 'object' ? String(o.value) : String(o)
    return { value, label: tOption(field, value, lang) }
  })
}

/** The sheet's own title. `doc` is `documentNode(translations)`; `fallback` is `template.name`, which
 *  lives on the template ROW — the one translatable string that was never in the schema at all, which
 *  is also what the recorded source is compared against: renaming the sheet is how this one goes
 *  stale. */
export function tDocumentName(doc, lang, fallback) {
  return slot(doc, lang, 'name', fallback) || fallback || ''
}

// ── the page around the form ──────────────────────────────────────────────────────────────────────
/**
 * Every user-visible string the QR page owns. Twenty-five of them, which is the entire app-chrome
 * surface a field hand ever sees — the other ~2,800 strings in this application are behind a login
 * they do not have.
 *
 * Keyed by id rather than by English text so the wording can be fixed without silently un-translating
 * the page. `t()` below falls back to English on a missing entry for exactly the same reason the form
 * resolvers do.
 */
export const CHROME = {
  en: {
    language: 'Language',
    equipment: 'Equipment',
    whoAreYou: 'Who are you?',
    yourName: 'Your name',
    badgeNumber: 'Badge number',
    pin: 'PIN',
    checking: 'Checking…',
    continue: 'Continue',
    signedInAs: 'Signed in as',
    sending: 'Sending…',
    send: 'Send',
    loading: 'Loading…',
    sent: 'Sent',
    closePage: 'You can close this page.',
    stickerNotRecognised: 'Sticker not recognised',
    clear: 'Clear',
    signHere: 'Sign here',
    signed: 'Signed',
    notSignedYet: 'Not signed yet',
    choose: '— choose —',
    // A count, not a sentence: "6 of 8 answered". Built by a function because the number order is not
    // guaranteed to survive a translation, even if it happens to here.
    answeredCount: (n, total) => `${n} of ${total} answered`,
    // ── the machine, and where it came from ──
    // A box that is filled in and cannot be typed in reads as broken unless it says why. This line is
    // the why, and it is also the promise the sticker makes: what is on the record is the machine the
    // label is on, not whatever anybody typed.
    fromTheSticker: 'From the sticker on this machine — it can’t be changed here.',
    wrongMachine: 'Wrong machine?',
    wrongMachineTitle: 'Is this label on the wrong machine?',
    wrongMachineBody: 'Fill this sheet in for the machine you are actually looking at, and tell the '
      + 'office. They will move the label. Nothing you send now changes.',
    whatMachine: 'What machine is it really?',
    tellTheOffice: 'Tell the office',
    reportSent: 'The office has been told. Carry on with the sheet.',
    reportFailed: 'That couldn’t be sent. Tell your foreman.',
    sticker: 'Sticker',
    blankSticker: 'This sticker is not on any one machine.',
    blankStickerHelp: 'Write the rental tag, unit number or serial of the machine you are inspecting.',
    // The page's own fallbacks, used only when the server sends no `detail` at all.
    stickerInvalid: 'That sticker isn’t valid.',
    identifyFailed: 'That didn’t work. Check with your foreman.',
    answersStillNeeded: 'Some answers are still needed.',
    sessionExpired: 'Your session expired. Enter your badge again.',
    sendFailed: 'That couldn’t be sent. Try again.',
    // THE CAP IS ON THE WHOLE FORM, NOT ON ONE QUESTION. It used to be applied per photo field
    // while the server counted the total, so a sheet with four photo fields let somebody attach
    // well past the limit, pass the client, and lose the entire submission to a 400 — and the
    // overflow was dropped in SILENCE, so they never knew either number was wrong.
    tooManyPhotos: ({ max, dropped }) =>
      `You can attach ${max} photos in total on this form, so ${dropped} `
      + `${dropped === 1 ? 'photo was' : 'photos were'} not added. Remove one to add another.`,
  },
  es: {
    language: 'Idioma',
    equipment: 'Equipo',
    whoAreYou: '¿Quién es usted?',
    yourName: 'Su nombre',
    badgeNumber: 'Número de gafete',
    pin: 'PIN',
    checking: 'Verificando…',
    continue: 'Continuar',
    // `Identificado` agrees masculine, and this line names the person reading it. Every other string
    // in this dictionary is gender-free; `Ingresó como` is a verb and so is too.
    signedInAs: 'Ingresó como',
    sending: 'Enviando…',
    send: 'Enviar',
    loading: 'Cargando…',
    sent: 'Enviado',
    closePage: 'Ya puede cerrar esta página.',
    stickerNotRecognised: 'Calcomanía no reconocida',
    clear: 'Borrar',
    signHere: 'Firme aquí',
    signed: 'Firmado',
    notSignedYet: 'Sin firmar',
    choose: '— elija —',
    answeredCount: (n, total) => `${n} de ${total} contestados`,
    fromTheSticker: 'Viene de la calcomanía de esta máquina — aquí no se puede cambiar.',
    wrongMachine: '¿Máquina equivocada?',
    wrongMachineTitle: '¿Esta calcomanía está en la máquina equivocada?',
    wrongMachineBody: 'Llene esta hoja para la máquina que tiene enfrente y avise a la oficina. Ellos '
      + 'cambian la calcomanía. Nada de lo que envíe ahora cambia.',
    whatMachine: '¿Qué máquina es en realidad?',
    tellTheOffice: 'Avisar a la oficina',
    reportSent: 'Ya se avisó a la oficina. Siga con la hoja.',
    reportFailed: 'No se pudo enviar. Avise a su supervisor.',
    sticker: 'Calcomanía',
    blankSticker: 'Esta calcomanía no pertenece a ninguna máquina.',
    blankStickerHelp: 'Escriba la etiqueta de renta, el número de unidad o el número de serie de la '
      + 'máquina que está inspeccionando.',
    stickerInvalid: 'Esa calcomanía no sirve.',
    identifyFailed: 'No funcionó. Consulte con su supervisor.',
    answersStillNeeded: 'Faltan respuestas.',
    sessionExpired: 'Su sesión venció. Ingrese su gafete otra vez.',
    sendFailed: 'No se pudo enviar. Intente otra vez.',
    tooManyPhotos: ({ max, dropped }) =>
      `Puede adjuntar ${max} fotos en total en este formulario, así que no se `
      + `${dropped === 1 ? 'agregó 1 foto' : `agregaron ${dropped} fotos`}. Quite una para agregar otra.`,
  },
}

/** A getter for the chrome in `lang`, falling back string-by-string to English. */
export function chrome(lang) {
  const dict = CHROME[lang] || CHROME[String(lang || '').split('-')[0]] || {}
  return (id, ...args) => {
    const v = dict[id] !== undefined && dict[id] !== '' ? dict[id] : CHROME[BASE_LANGUAGE][id]
    return typeof v === 'function' ? v(...args) : (v === undefined ? '' : v)
  }
}

/**
 * The server's own prose, translated on this side.
 *
 * ⚠ THE HONEST LIMITATION. Nineteen user-visible strings on this page are produced by the SERVER —
 * `_NOT_FOUND`, `GENERIC_FAILURE`, the throttle and upload refusals — and printed by the page as
 * `body.detail`. The response carries no message code to switch on, only English prose, so the only
 * client-side fix is this exact-match table. It is brittle by construction: reword a message in Python
 * and its Spanish silently stops appearing. A pytest drift guard
 * (test_public_inspection.TestTheServerProseIsTranslatedOnThePage) AST-reads every fixed `detail=`
 * literal out of the three server files and fails if one is not covered here — and fails the other way
 * too, on an entry that translates text no server file produces any more. The durable fix is a stable
 * `code` beside `detail`; it is not built, and that guard is what stands in for it.
 *
 * Three server messages are NOT covered and cannot be, because they interpolate a number, a list, or
 * another error's text: `f"You can attach at most {n} photos."`, `f"This form doesn't take a file for:
 * …"` and the `str(exc)` passthrough. Those print in English, which is why `fallback` is returned
 * unchanged rather than blanked.
 */
export const SERVER_MESSAGES = {
  es: {
    "That sticker isn't valid. Check with your foreman.":
      'Esa calcomanía no sirve. Consulte con su supervisor.',
    'Too many tries from this phone. Wait a minute.':
      'Demasiados intentos desde este teléfono. Espere un minuto.',
    'Too many tries at this sticker. Wait a minute.':
      'Demasiados intentos en esta calcomanía. Espere un minuto.',
    "That badge number and PIN don't match. Check with your foreman.":
      'Ese número de gafete y ese PIN no coinciden. Consulte con su supervisor.',
    'Your session expired. Enter your badge again.':
      'Su sesión venció. Ingrese su gafete otra vez.',
    'That form is too big to send. Tell your foreman.':
      'Esta hoja pesa demasiado para enviarse. Avise a su supervisor.',
    "That form couldn't be read. Try again.":
      'No se pudo leer la hoja. Intente otra vez.',
    "This machine's sheet has been filled in a lot today. Tell your foreman.":
      'Esta hoja ya se llenó muchas veces hoy para esta máquina. Avise a su supervisor.',
    'Only photos and PDFs can be attached.':
      'Solo se pueden adjuntar fotos y archivos PDF.',
    'One of those photos is too large (8 MB max).':
      'Una de las fotos pesa demasiado (máximo 8 MB).',
    'One of those photos was empty.':
      'Una de las fotos venía vacía.',
    'Those photos are too large altogether.':
      'Todas las fotos juntas pesan demasiado.',
    "One of those files isn't really a photo.":
      'Uno de los archivos no es realmente una foto.',
    "This form isn't available right now. Tell your foreman.":
      'Esta hoja no está disponible ahora. Avise a su supervisor.',
    "Your photos couldn't be saved. Try again.":
      'No se pudieron guardar sus fotos. Intente otra vez.',
    "That form couldn't be saved. Try again.":
      'No se pudo guardar la hoja. Intente otra vez.',
  },
}

export function serverMessage(text, lang) {
  if (typeof text !== 'string' || !text) return text
  return (SERVER_MESSAGES[lang] || {})[text] || text
}

// ── choosing the language ─────────────────────────────────────────────────────────────────────────
//
// THIS PAGE KEEPS ITS OWN STORAGE KEY, and that is a decision rather than an oversight. The choice
// made here is made by an anonymous person at a sticker, among the languages THAT SHEET is written in
// — a set derived from the tenant's translation rows, which is not the set of languages the
// application is translated into. The application reads this key as a secondary signal (see
// shared/i18n/languages.readStoredLanguage) so a foreman who chose Español at a machine and then logs
// in on the same phone does not choose twice. One-directional on purpose: nothing in the application
// can move the language of a sheet somebody is standing over.
export const LANGUAGE_STORAGE_KEY = PUBLIC_LANGUAGE_STORAGE_KEY

function readStored() {
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY) || ''
  } catch (err) {
    return ''   // Safari private mode throws on localStorage; a language preference is not worth a crash.
  }
}

export function rememberLanguage(tag) {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, tag)
  } catch (err) { /* see readStored */ }
}

/**
 * Which language to open in, highest priority first:
 *
 *   1. an explicit choice made on this page before. A hand uses the same phone at twenty machines of
 *      one employer; asking twenty times is the bug. localStorage rather than a cookie — a cookie on
 *      this origin would ride along on the API calls and start to look like state the server may use,
 *      and the whole point is that the server never learns the language.
 *   2. the phone's own language, **but only when it is not the base language**. This one asymmetry is
 *      load-bearing. A phone can move you OFF English and never back onto it, because almost every
 *      handset on a US job site reports `en-US` whatever its owner reads — so a plain "device wins"
 *      rule would make `public_default_language` inert for exactly the tenant who set it. A
 *      Spanish-locale phone still opens in Spanish at an English-default tenant, which is the case
 *      the rule exists to serve.
 *   3. the tenant's default (`public_default_language`). This is what serves the hand holding a
 *      company phone that nobody ever changed — the tenant knows its crew.
 *   4. the sheet's base language. Never a blank page.
 *
 * ONE IMPLEMENTATION. The chain itself now lives in shared/i18n/languages.resolveLanguage, which the
 * whole application resolves through and which a pytest mirrors against src/shared/i18n/languages.py.
 * This wrapper supplies the two things that are particular to a SHEET rather than to the application:
 * `offered` is the languages that version is written in (derived from its translation rows, never
 * declared), and `base` is `offered[0]` — a sheet authored in Spanish falls back to Spanish, so the
 * device asymmetry has to be measured against the sheet's own base and not against English.
 *
 * The account rung is deliberately absent: the person reading this page has no account. That is the
 * whole premise of the page.
 */
export function pickLanguage({ available, stored, deviceLanguages, tenantDefault }) {
  const offered = (available || []).filter(Boolean)
  if (!offered.length) return BASE_LANGUAGE
  // The phone's own list, read the way this page has always read it. NOT shared's `deviceLanguages()`
  // helper, which prefers `navigator.languages`: this page has only ever consulted `navigator.language`
  // and widening that here would change which sheet a hand opens, in a commit about file layout.
  const device = (deviceLanguages && deviceLanguages.length
    ? deviceLanguages
    : [typeof navigator !== 'undefined' ? navigator.language : ''])
  return resolveLanguage({
    storedChoice: stored !== undefined ? stored : readStored(),
    deviceLanguages: device,
    tenantDefault,
    offered,
    base: offered[0],
  })
}
